import React from "react";
import { useMemo, useRef, useCallback } from "react";
import { Segment, Header, Button } from "semantic-ui-react";
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import OntologyClassesTable from "./OntologyClassesTable";
import AcronymsSidebar from "./AcronymsSidebar";
import CustomSetFilter, { customSetFilterDoesFilterPass } from "./CustomSetFilter";
import { DEFAULT_COLUMNS, MISSING_VALUES, getNodeClasses } from "../utils";

// Register all Community features
ModuleRegistry.registerModules([AllCommunityModule]);

const LSR_META = {
  1: {
    title:
      "Pro-dopaminergic pharmacological interventions for anhedonia in depression",
    link: "https://wellcomeopenresearch.org/articles/8-425",
  },
  2: {
    title:
      "Mechanisms through which exercise reduces symptom severity and/or functional impairment in posttraumatic stress disorder (PTSD)",
    link: "https://wellcomeopenresearch.org/articles/8-494/v2",
  },
  3: {
    title: "Trace amine-associated receptor 1 (TAAR1) agonism for psychosis",
    link: "https://wellcomeopenresearch.org/articles/9-182",
  },
  6: {
    title: "Cognitive bias modification for social anxiety: protocol for a living systematic review of human studies and meta-analysis",
    link: "https://wellcomeopenresearch.org/articles/9-657/v2",
  },
};

const DataViewer = ({
  data,
  selectedColumns,
  ontologyDictionary,
  understandableLabelsDictionary,
  acronymsData,
}) => {
  const gridRef = useRef();
  const columnsToRender = ["LSR #", ...selectedColumns];

  const filteredData = useMemo(() => {
    return data.filter((row) => {
      const filtereableColumns = columnsToRender.filter(
        (column) => !DEFAULT_COLUMNS.includes(column)
      );

      // if no columns are filtereable, we don't need to filter
      if (filtereableColumns.length === 0) {
        return true;
      }

      // check if all filtereable columns have missing values for this row
      const missingValuesOnAllColumns = filtereableColumns.every((column) =>
        MISSING_VALUES.includes(row[column])
      );

      return !missingValuesOnAllColumns;
    });
  }, [data, columnsToRender]);

  const getColumnClasses = (column) => {
    const classIds = understandableLabelsDictionary[column];
    if (!classIds) return [];

    return getNodeClasses(ontologyDictionary, classIds);
  };

  // LSR Button Cell Renderer Component
  const LSRButtonCellRenderer = useCallback(props => {
    const value = props.value;
    if (!LSR_META[value]) {
      return <strong>{value}</strong>;
    }

    return (
      <a
        href={LSR_META[value].link}
        className="small ui button lsr-button"
        target="_blank"
        rel="noopener noreferrer"
      >
        <strong>{value}</strong>
      </a>
    );
  }, []);

  const DOILinkCellRenderer = useCallback(props => {
    const value = props.value;

    if (!value || MISSING_VALUES.includes(value)) {
      return null;
    }

    const doi = value.toString().trim();

    let doiUrl;
    if (doi.startsWith('http://') || doi.startsWith('https://')) {
      doiUrl = doi;
    } else {
      doiUrl = `https://doi.org/${doi}`;
    }

    return (
      <a
        href={doiUrl}
        target="_blank"
        rel="noopener noreferrer"
      >
        {doi}
      </a>
    );
  }, []);

  // AG Grid Column Definitions
  const columnDefs = useMemo(() => {
    const getBaseColumnDef = (column, pinned) => ({
      pinned,
      width: pinned ? 180 : null,
      field: column,
      headerName: column,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      headerTooltip: true,
      resizable: true,
      filter: {
        component: CustomSetFilter,
        doesFilterPass: customSetFilterDoesFilterPass,
      },
      cellClass: params => params.value == null ? 'no-data' : '',
    });

    const getOntologyTooltip = () => (props) => {
      const { column } = props;
      const columnName = column.getColDef().field;
      const classes = getColumnClasses(columnName);
      return (<OntologyClassesTable classes={classes} />);
    };

    return columnsToRender.map(column => {
      const pinned = DEFAULT_COLUMNS.includes(column);

      if (column === "LSR #") {
        return {
          ...getBaseColumnDef(column, pinned),
          width: 100,
          cellRenderer: LSRButtonCellRenderer,
          cellStyle: { textAlign: 'center' },
          tooltipValueGetter: (params) => {
            const value = params.value;
            return (value && LSR_META[value]) ? LSR_META[value].title : null;
          },
        };
      }

      if (column === "DOI of study") {
        return {
          ...getBaseColumnDef(column, pinned),
          cellRenderer: DOILinkCellRenderer,
          tooltipComponent: getOntologyTooltip(),
        };
      }

      return {
        ...getBaseColumnDef(column, pinned),
        tooltipComponent: getOntologyTooltip(),
      };
    });
  }, [columnsToRender]);

  // AG Grid configuration
  const gridOptions = useMemo(() => ({
    defaultColDef: {
      sortable: true,
      resizable: true,
      filter: true,
      cellDataType: false,
    },
    columnTypes: {
      numberColumn: {
        cellStyle: { textAlign: 'center' },
      },
      textColumn: {
        cellStyle: { textAlign: 'left' },
      }
    },
    suppressScrollOnNewData: true,
  }), []);

  return (
    <>
      <div className="data-table-header">
        <Header as="h2">Data Table</Header>
        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          <AcronymsSidebar acronymsData={acronymsData} />
          <Button onClick={() => gridRef.current.api.exportDataAsCsv()} primary>Download CSV</Button>
        </div>
      </div>
      {selectedColumns.length === 0 ? (
        <Segment>
          <p>Select columns to view data</p>
        </Segment>
      ) : (
          <>
            <div className="data-table ag-theme-alpine" style={{ height: '80vh', width: '100%' }}>
              <AgGridReact
                ref={gridRef}
                rowData={filteredData}
                columnDefs={columnDefs}
                gridOptions={gridOptions}
                tooltipShowDelay={0}
                tooltipHideDelay={10000}
                enableRowPinning={true}
                enableCellTextSelection={true}
                suppressFieldDotNotation={true}
                enableFilterHandlers={true}
              />
              {filteredData.length === 0 && (
                <Segment>
                  <p>No data available for your selection.</p>
                </Segment>
              )}
            </div>
            <div className="data-table-footnote">
              <strong>Note:</strong> Blank cells indicate the data field was included in the LSR file but left blank.
              <span className="no-data-example">Red highlighted cells</span> indicate the data field was not included in the LSR file.
            </div>
          </>
      )}
    </>
  );
};

export default DataViewer;
